// Assembly and Takeoff Assistant APIs
import type { PaginatedResponse } from '@quotech/api'
import { getApiClient, withOrgConfig, asArray } from '../quote-api-helpers'
import type {
  Assembly, AssemblyDetail, CreateAssemblyRequest,
  AssemblyLineItem, AssemblyLineItemData,
  TakeoffSuggestionsResult, DocumentTakeoffResult,
} from '../quote-api-assembly-types'
import type { QuoteLineItem } from '../quote-api-types'

export const assemblyTakeoffApi = {
  // =========================================================================
  // ASSEMBLY APIs
  // =========================================================================

  async listAssemblies(params?: { search?: string; category?: string; popular_only?: boolean }): Promise<Assembly[]> {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<Assembly> | Assembly[]>(
      `/quotebuilder/assemblies/`,
      withOrgConfig({ params })
    )
    return asArray(res.data)
  },

  async searchAssemblies(query: string, category?: string, popularOnly?: boolean): Promise<{ results: Assembly[]; count: number }> {
    const client = getApiClient()
    const params: Record<string, string | boolean> = {}
    if (query) params.query = query
    if (category) params.category = category
    if (popularOnly) params.popular_only = true

    const res = await client.get<{ results: Assembly[]; count: number }>(
      `/quotebuilder/assemblies/search/`,
      withOrgConfig({ params })
    )
    return res.data
  },

  async getAssembly(id: string): Promise<AssemblyDetail> {
    const client = getApiClient()
    const res = await client.get<AssemblyDetail>(`/quotebuilder/assemblies/${id}/`, withOrgConfig())
    return res.data
  },

  async createAssembly(data: CreateAssemblyRequest): Promise<AssemblyDetail> {
    const client = getApiClient()
    const res = await client.post<AssemblyDetail>(`/quotebuilder/assemblies/`, data, withOrgConfig())
    return res.data
  },

  async updateAssembly(id: string, data: Partial<CreateAssemblyRequest>): Promise<AssemblyDetail> {
    const client = getApiClient()
    const res = await client.patch<AssemblyDetail>(`/quotebuilder/assemblies/${id}/`, data, withOrgConfig())
    return res.data
  },

  async deleteAssembly(id: string): Promise<void> {
    const client = getApiClient()
    await client.delete(`/quotebuilder/assemblies/${id}/`, withOrgConfig())
  },

  async insertAssembly(
    assemblyId: string,
    sectionId: string,
    options?: { quantityMultiplier?: number; insertAfterItemId?: string }
  ): Promise<{
    message: string
    items: QuoteLineItem[]
    section_id: string
    assembly_id: string
  }> {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/assemblies/${assemblyId}/insert/`,
      {
        section_id: sectionId,
        quantity_multiplier: options?.quantityMultiplier ?? 1.0,
        insert_after_item_id: options?.insertAfterItemId ?? null,
      },
      withOrgConfig()
    )
    return res.data
  },

  async getAssemblyCategories(): Promise<string[]> {
    const client = getApiClient()
    const res = await client.get<string[]>(`/quotebuilder/assemblies/categories/`, withOrgConfig())
    return res.data
  },

  async duplicateAssembly(id: string): Promise<AssemblyDetail> {
    const client = getApiClient()
    const res = await client.post<AssemblyDetail>(`/quotebuilder/assemblies/${id}/duplicate/`, {}, withOrgConfig())
    return res.data
  },

  async addAssemblyItem(assemblyId: string, data: AssemblyLineItemData): Promise<AssemblyLineItem> {
    const client = getApiClient()
    const res = await client.post<AssemblyLineItem>(
      `/quotebuilder/assemblies/${assemblyId}/add_item/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async removeAssemblyItem(assemblyId: string, itemId: string): Promise<void> {
    const client = getApiClient()
    await client.delete(`/quotebuilder/assemblies/${assemblyId}/items/${itemId}/`, withOrgConfig())
  },

  // =========================================================================
  // TAKEOFF ASSISTANT APIs
  // =========================================================================

  /**
   * Get AI-powered takeoff suggestions for a line item or context.
   * Returns similar rows, suggested applications, and coverage gaps.
   */
  async getTakeoffSuggestions(
    quoteId: string,
    params: {
      line_item_id?: string
      section_id?: string
      description?: string
      location?: string
    }
  ): Promise<TakeoffSuggestionsResult> {
    try {
      const client = getApiClient()
      const res = await client.post<TakeoffSuggestionsResult>(
        `/quotebuilder/ai/takeoff/${quoteId}/suggest/`,
        params,
        withOrgConfig()
      )
      return res.data
    } catch (err) {
      console.error('Takeoff suggestions failed:', err)
      return {
        similar_rows: [],
        suggested_applications: [],
        coverage_gaps: [],
        context_items: [],
      }
    }
  },

  /**
   * Apply an application to a line item during takeoff.
   */
  async applyTakeoffApplication(
    quoteId: string,
    lineItemId: string,
    applicationId: string
  ): Promise<QuoteLineItem> {
    const client = getApiClient()
    const res = await client.post<QuoteLineItem>(
      `/quotebuilder/ai/takeoff/${quoteId}/apply/`,
      {
        line_item_id: lineItemId,
        application_id: applicationId,
      },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Get document-based takeoff suggestions from attached files.
   * Extracts items from PDFs, drawings, and scope documents.
   */
  async getDocumentTakeoffItems(
    quoteId: string,
    attachmentId?: string
  ): Promise<DocumentTakeoffResult> {
    try {
      const client = getApiClient()
      const params: Record<string, string> = {}
      if (attachmentId) params.attachment_id = attachmentId

      const res = await client.get<DocumentTakeoffResult>(
        `/quotebuilder/quotes/${quoteId}/document-takeoff/`,
        withOrgConfig({ params })
      )
      return res.data
    } catch (err) {
      console.error('Document takeoff failed:', err)
      return {
        items: [],
        source_documents: [],
        extraction_status: 'error',
      }
    }
  },
}
